import React, { useRef, useState } from "react";
import OrgNummer from "personnummer.js";
import { useTranslation } from "react-i18next";
import { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { useHistory } from "react-router";
import type { BaseButtonStyle } from "components/designV2/buttons/baseButton";
import { InputField } from "components/designV2/inputfield/inputfield";
import { ProgressIndicator } from "components/designV2/progressIndicator/progressIndicator.component";
import { useAuthStore } from "stores/authStore/authStore";
import { ColorV2 } from "config/colors";
import { Api } from "services/api/api.service";
import { LocationMap } from "web-apps/company/components/location/locationMap.component";
import { hasStreetNumber } from "web-apps/company/components/location/locationMap.logic";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  formatCityName,
  hasCompleteAddress,
  parseCompanyInformationResponse,
} from "./companyInfoForm.logic";
import {
  ActionsRow,
  AddressField,
  BackButton,
  FieldLabel,
  ContinueButton,
  FieldsWrapper,
  StepLabel,
  StyledForm,
  StyledTitle,
} from "./companyInfoForm.styled";

const CONTINUE_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  backgroundColor: ColorV2.PositiveGreen,
  hoverBackgroundColor: ColorV2.SimflexityGreen,
  activeBackgroundColor: ColorV2.SimflexityGreen,
  disabledBackgroundColor: ColorV2.PositiveGreenLight,
  disabledTextColor: ColorV2.White,
  disabledBorderColor: "transparent",
};

type Props = {
  city: string;
  companyAdress: string;
  companyName: string;
  legalName: string;
  location: string;
  page: number;
  postalCode: string;
  registrationNumber: string;
  setCity: React.Dispatch<React.SetStateAction<string>>;
  setCompanyAdress: React.Dispatch<React.SetStateAction<string>>;
  setCompanyName: React.Dispatch<React.SetStateAction<string>>;
  setLegalName: React.Dispatch<React.SetStateAction<string>>;
  setLocation: React.Dispatch<React.SetStateAction<string>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setPostalCode: React.Dispatch<React.SetStateAction<string>>;
  setRegistrationNumber: React.Dispatch<React.SetStateAction<string>>;
};

export const CompanyInfoForm: React.FC<Props> = ({
  city,
  companyAdress,
  companyName,
  legalName,
  location,
  page,
  postalCode,
  registrationNumber,
  setCity,
  setCompanyAdress,
  setCompanyName,
  setLegalName,
  setLocation,
  setPage,
  setPostalCode,
  setRegistrationNumber,
}) => {
  const { t } = useTranslation();
  const requiredLabel = t("System.Required").toLowerCase();
  const history = useHistory();
  const [, authActions] = useAuthStore();
  const [isRegistrationNumberInvalid, setIsRegistrationNumberInvalid] =
    useState(false);
  const [showRegistrationNumberError, setShowRegistrationNumberError] =
    useState(false);
  const [isCompanyInformationLoading, setIsCompanyInformationLoading] =
    useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [companyState] = useCompanyStore();
  const companyInformationRequestIdRef = useRef(0);

  const hasValue = (value: string): boolean => value.trim().length > 0;
  const isAddressMissingStreetNumber =
    hasValue(companyAdress) && !hasStreetNumber(companyAdress);

  const validateAddress = (): boolean => {
    return (
      hasValue(city) &&
      hasValue(companyAdress) &&
      hasValue(postalCode) &&
      hasValue(location) &&
      !isAddressMissingStreetNumber
    );
  };

  const validate = (): boolean => {
    return (
      hasValue(companyName) &&
      hasValue(registrationNumber) &&
      hasValue(legalName) &&
      hasValue(city) &&
      validateAddress() &&
      !isRegistrationNumberInvalid
    );
  };

  const clearCompanyInformationFields = (): void => {
    setLegalName("");
    setCompanyAdress("");
    setPostalCode("");
    setCity("");
    setLocation("");
  };

  const validateRegistrationNumber = (value: string): boolean => {
    const requestId = companyInformationRequestIdRef.current + 1;
    companyInformationRequestIdRef.current = requestId;
    clearCompanyInformationFields();

    if (OrgNummer.validateCIN(value)) {
      setIsRegistrationNumberInvalid(false);
      setIsCompanyInformationLoading(true);
      void fetchCompanyInfo(value, requestId);
      return true;
    } else {
      setIsRegistrationNumberInvalid(true);
      setIsCompanyInformationLoading(false);
      return false;
    }
  };

  const fetchCompanyInfo = async (
    number: string,
    requestId: number,
  ): Promise<void> => {
    const isCurrentRequest = (): boolean =>
      companyInformationRequestIdRef.current === requestId;
    const setLookupError = (): void => {
      if (isCurrentRequest()) {
        setIsCompanyInformationLoading(false);
      }
    };

    try {
      const response = await Api().company.getCompanyInformation(number);

      if (!isCurrentRequest()) {
        return;
      }

      const companyInformation = parseCompanyInformationResponse(response.data);

      if (!companyInformation) {
        setLookupError();
        return;
      }

      if (companyInformation.namn) {
        setLegalName(companyInformation.namn);
      }

      if (!hasCompleteAddress(companyInformation.adress)) {
        setIsCompanyInformationLoading(false);
        return;
      }

      const companyAddress = companyInformation.adress;
      const formattedCity = formatCityName(companyAddress.postort);

      setCompanyAdress(companyAddress.postadress);
      setPostalCode(companyAddress.postnummer);
      setCity(formattedCity);
      setIsCompanyInformationLoading(false);

      try {
        const geoData = await geocodeByAddress(
          `${companyAddress.postadress}, ${companyAddress.postort}`,
        );

        if (!isCurrentRequest()) {
          return;
        }

        const firstGeoResult = geoData[0];

        if (!firstGeoResult) {
          return;
        }

        const geoLocation = await getLatLng(firstGeoResult);

        if (!isCurrentRequest()) {
          return;
        }

        if (
          Number.isFinite(geoLocation.lat) &&
          Number.isFinite(geoLocation.lng)
        ) {
          setLocation(`${geoLocation.lat}, ${geoLocation.lng}`);
        }
      } catch {
        if (isCurrentRequest()) {
          setIsCompanyInformationLoading(false);
        }
      }
    } catch {
      setLookupError();
    }
  };

  const cancelRegistration = async (): Promise<void> => {
    if (companyState.company) {
      history.push("/company/schedule");
      return;
    }

    try {
      await authActions.signOut();
    } catch {
      // Cancellation should still return the user to login if sign-out cleanup fails.
    }

    history.push("./login");
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isAdvancing) {
      return;
    }

    if (validate()) {
      setIsAdvancing(true);
      setPage(page + 1);
    }
  };

  return (
    <StyledForm onSubmit={handleSubmit}>
      <StepLabel>
        {t("RegisterCompany.StepOfTotal", { step: 1, total: 2 })}
      </StepLabel>
      <ProgressIndicator steps={2} currentStep={1} />
      <StyledTitle>{t("RegisterCompany.CompanyInformation")}</StyledTitle>
      <FieldsWrapper>
        <InputField
          label={`${t("RegisterCompany.RegistrationNumber")} (${requiredLabel})`}
          type="text"
          value={registrationNumber}
          placeholder="XXXXXX-XXXX"
          fullWidth
          hasError={showRegistrationNumberError && isRegistrationNumberInvalid}
          errorText={t("RegisterCompany.InvalidRegistrationNumber")}
          onChange={(value) => {
            const trimmedValue = value.trim();
            validateRegistrationNumber(trimmedValue);
            setRegistrationNumber(trimmedValue);
          }}
          onBlur={() => setShowRegistrationNumberError(true)}
          onClear={() => {
            companyInformationRequestIdRef.current += 1;
            setRegistrationNumber("");
            setIsRegistrationNumberInvalid(false);
            setShowRegistrationNumberError(false);
            setIsCompanyInformationLoading(false);
            clearCompanyInformationFields();
          }}
        />
        <InputField
          label={`${t("RegisterCompany.LegalName")} (${requiredLabel})`}
          type="text"
          value={legalName}
          placeholder={t("RegisterCompany.LegalName")}
          fullWidth
          onChange={setLegalName}
          onClear={() => setLegalName("")}
        />
        <AddressField>
          <FieldLabel>
            {`${t("RegisterCompany.CompanyAddress")} (${requiredLabel})`}
          </FieldLabel>
          <LocationMap
            key={registrationNumber}
            address={companyAdress && city ? `${companyAdress}, ${city}` : ""}
            setAddress={setCompanyAdress}
            setCity={setCity}
            setPostalCode={setPostalCode}
            setLocation={setLocation}
            inRegistration={true}
            noMap
            hasIncompleteAddress={!validateAddress() && hasValue(companyAdress)}
          />
        </AddressField>
        <InputField
          label={`${t("RegisterCompany.DisplayName")} (${requiredLabel})`}
          type="text"
          value={companyName}
          placeholder={t("RegisterCompany.DisplayName")}
          fullWidth
          onChange={setCompanyName}
          onClear={() => setCompanyName("")}
        />
      </FieldsWrapper>

      <ActionsRow>
        <BackButton
          type="button"
          label={t("General.Back")}
          onClick={cancelRegistration}
        />
        <ContinueButton
          type="submit"
          label={t("RegisterCompany.Continue")}
          disabled={
            !validate() ||
            page !== 0 ||
            isAdvancing ||
            isCompanyInformationLoading
          }
          styleOverrides={CONTINUE_BUTTON_STYLES}
        />
      </ActionsRow>
    </StyledForm>
  );
};
